import { ProductsApiService, RecipeApiService } from '@adminServices';
import * as RewardsActions from '@adminStore/rewards/rewards.actions';
import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { NotificationService } from '@shared';
import { of } from 'rxjs';
import { catchError, map, switchMap, withLatestFrom } from 'rxjs/operators';
import * as ProductsActions from './products.actions';
import { selectProductsParams } from './products.selectors';

@Injectable()
export class ProductsEffects {
  private actions$ = inject(Actions);
  private store = inject(Store);
  private productsApi = inject(ProductsApiService);
  private recipeApi = inject(RecipeApiService);
  private notification = inject(NotificationService);

  loadProducts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductsActions.loadProducts),
      switchMap(({ params }) =>
        this.productsApi.findAll(params).pipe(
          map((response) => ProductsActions.loadProductsSuccess({ response })),
          catchError((error: any) => {
            this.notification.error('admin.notifications.load_products_error');
            return of(ProductsActions.loadProductsFailure({
              error: error.message || 'Failed to load products',
            }));
          }),
        ),
      ),
    ),
  );

  createProduct$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductsActions.createProduct),
      switchMap(({ productData, recipe }) =>
        this.productsApi.create(productData).pipe(
          switchMap(product => {
            if (recipe && recipe.items.length > 0) {
              return this.recipeApi.setRecipe(product.id, recipe).pipe(
                map(() => {
                  this.notification.success('admin.notifications.product_created');
                  return ProductsActions.createProductSuccess({ product });
                }),
                catchError(() => {
                  this.notification.error('admin.recipe_tab.save_error');
                  return of(ProductsActions.createProductSuccess({ product }));
                }),
              );
            }
            this.notification.success('admin.notifications.product_created');
            return of(ProductsActions.createProductSuccess({ product }));
          }),
          catchError((error: any) => {
            this.notification.error('admin.notifications.create_product_error');
            return of(ProductsActions.createProductFailure({
              error: error.message || 'Failed to create product',
            }));
          }),
        ),
      ),
    ),
  );

  updateProduct$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductsActions.updateProduct),
      switchMap(({ id, productData, recipe, deleteRecipe }) =>
        this.productsApi.update(id, productData).pipe(
          switchMap(product => {
            if (deleteRecipe) {
              return this.recipeApi.deleteRecipe(id).pipe(
                map(() => {
                  this.notification.success('admin.notifications.product_updated');
                  return ProductsActions.updateProductSuccess({ product });
                }),
                catchError(() => {
                  this.notification.error('admin.recipe_tab.delete_error');
                  return of(ProductsActions.updateProductSuccess({ product }));
                }),
              );
            }
            if (recipe && recipe.items.length > 0) {
              return this.recipeApi.setRecipe(id, recipe).pipe(
                map(() => {
                  this.notification.success('admin.notifications.product_updated');
                  return ProductsActions.updateProductSuccess({ product });
                }),
                catchError(() => {
                  this.notification.error('admin.recipe_tab.save_error');
                  return of(ProductsActions.updateProductSuccess({ product }));
                }),
              );
            }
            this.notification.success('admin.notifications.product_updated');
            return of(ProductsActions.updateProductSuccess({ product }));
          }),
          catchError((error: any) => {
            this.notification.error('admin.notifications.update_product_error');
            return of(ProductsActions.updateProductFailure({
              error: error.message || 'Failed to update product',
            }));
          }),
        ),
      ),
    ),
  );

  deleteProduct$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductsActions.deleteProduct),
      switchMap(({ id, deletionParams }) =>
        this.productsApi.delete(id, deletionParams).pipe(
          map(() => {
            this.notification.success('admin.notifications.product_deleted');
            return ProductsActions.deleteProductSuccess({ id });
          }),
          catchError((error: any) => {
            this.notification.error('admin.notifications.delete_product_error');
            return of(ProductsActions.deleteProductFailure({
              error: error.message || 'Failed to delete product',
            }));
          }),
        ),
      ),
    ),
  );

  reloadAfterChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        ProductsActions.createProductSuccess,
        ProductsActions.updateProductSuccess,
        ProductsActions.deleteProductSuccess,
      ),
      withLatestFrom(this.store.select(selectProductsParams)),
      switchMap(([ , params ]) => [
        RewardsActions.loadRewards({ params: { page: 1, limit: 200, sortBy: 'name' } }),
        ProductsActions.loadProducts({ params }),
      ]),
    ),
  );
}
